const state = {
  rewards: {}, // Keyed by programId: { [programId]: [rewards] }
  availableRewards: {}, // Keyed by programId: { [programId]: [rewards] }
  loading: false,
  error: null,
  availableRewardsCache: {}, // Keyed by programId: { [programId]: { data, timestamp } }
  cacheTimeout: 2 * 60 * 1000 // 2 minutes
}

const mutations = {
  SET_REWARDS (state, { programId, rewards }) {
    state.rewards = {
      ...state.rewards,
      [programId]: rewards
    }
  },
  SET_AVAILABLE_REWARDS (state, { programId, rewards, timestamp }) {
    state.availableRewards = {
      ...state.availableRewards,
      [programId]: rewards
    }
    state.availableRewardsCache = {
      ...state.availableRewardsCache,
      [programId]: { data: rewards, timestamp }
    }
  },
  ADD_REWARD (state, { programId, reward }) {
    const programRewards = state.rewards[programId] || []
    state.rewards = {
      ...state.rewards,
      [programId]: [...programRewards, reward]
    }
  },
  UPDATE_REWARD (state, { programId, reward }) {
    const programRewards = state.rewards[programId] || []
    const index = programRewards.findIndex(r => r.id === reward.id)
    if (index !== -1) {
      programRewards.splice(index, 1, reward)
      state.rewards = {
        ...state.rewards,
        [programId]: [...programRewards]
      }
    }

    // Update available rewards if present
    const availableRewards = state.availableRewards[programId] || []
    const availableIndex = availableRewards.findIndex(r => r.id === reward.id)
    if (availableIndex !== -1) {
      availableRewards.splice(availableIndex, 1, reward)
      state.availableRewards = {
        ...state.availableRewards,
        [programId]: [...availableRewards]
      }
    }
  },
  REMOVE_REWARD (state, { programId, rewardId }) {
    const programRewards = state.rewards[programId] || []
    state.rewards = {
      ...state.rewards,
      [programId]: programRewards.filter(r => r.id !== rewardId)
    }

    const availableRewards = state.availableRewards[programId] || []
    state.availableRewards = {
      ...state.availableRewards,
      [programId]: availableRewards.filter(r => r.id !== rewardId)
    }
  },
  SET_LOADING (state, loading) {
    state.loading = loading
  },
  SET_ERROR (state, error) {
    state.error = error
  }
}

const actions = {
  async fetchRewards ({ commit }, programId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const rewards = await this._vm.$loyaltyService.getRewards(programId)
      commit('SET_REWARDS', { programId, rewards })
      return rewards
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAvailableRewards ({ commit, state }, programId) {
    // Check cache (2 minutes)
    const cache = state.availableRewardsCache[programId]
    if (cache && cache.timestamp) {
      const cacheAge = Date.now() - cache.timestamp
      if (cacheAge < state.cacheTimeout) {
        return cache.data
      }
    }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const rewards = await this._vm.$loyaltyService.getAvailableRewards(programId)
      commit('SET_AVAILABLE_REWARDS', { programId, rewards, timestamp: Date.now() })
      return rewards
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createReward ({ commit }, data) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const reward = await this._vm.$loyaltyService.createReward(data)
      commit('ADD_REWARD', { programId: data.loyalty_program_id, reward })
      return reward
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateReward ({ commit }, { id, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const reward = await this._vm.$loyaltyService.updateReward(id, data)
      commit('UPDATE_REWARD', { programId: data.loyalty_program_id || reward.loyalty_program_id, reward })
      return reward
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteReward ({ commit }, { id, programId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await this._vm.$loyaltyService.deleteReward(id)
      commit('REMOVE_REWARD', { programId, rewardId: id })
      return true
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const getters = {
  rewardsByProgram: state => programId => {
    return state.rewards[programId] || []
  },
  availableRewardsByProgram: state => programId => {
    return state.availableRewards[programId] || []
  },
  affordableRewards: (state, getters) => (programId, points) => {
    const rewards = getters.availableRewardsByProgram(programId)
    return rewards.filter(r => r.points_required <= points)
  },
  loading: state => state.loading,
  error: state => state.error
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
